import type { AgeBand } from '@/game/coach/phrases';
import type { LayerId } from './agentLook';

/**
 * The agent builder's steps and what Agent Alex says at each (docs/design/agent-builder.md), per
 * age band. A step picks an item of a layer, a colour of a layer, or the codename. Each line is
 * also the voice menu's question (with the options read from the screen).
 */
export type Step =
  { kind: 'item'; layer: LayerId } | { kind: 'colour'; layer: LayerId } | { kind: 'codename' };

export const STEPS: readonly Step[] = [
  { kind: 'item', layer: 'body' },
  { kind: 'item', layer: 'face' },
  { kind: 'item', layer: 'hair' },
  { kind: 'colour', layer: 'hair' },
  { kind: 'item', layer: 'suit' },
  { kind: 'colour', layer: 'suit' },
  { kind: 'item', layer: 'eyewear' },
  { kind: 'item', layer: 'headgear' },
  { kind: 'item', layer: 'back' },
  { kind: 'item', layer: 'shoes' },
  { kind: 'codename' },
];

export const stepKey = (s: Step) => (s.kind === 'codename' ? 'codename' : `${s.kind}:${s.layer}`);

type Lines = Readonly<Record<string, string>>;

/** What Alex asks at each step, by `stepKey`. */
export const STEP_LINES: Readonly<Record<AgeBand, Lines>> = {
  younger: {
    'item:body':
      '[warm] Let’s make your agent! First, pick a skin colour, the one that feels most like you.',
    'item:face': '[warm] Pick a face you like.',
    'item:hair': '[playful] Now your hair. Which one?',
    'colour:hair': '[playful] Pick a hair colour!',
    'item:suit': '[excited] Every agent needs a suit. Which one do you like?',
    'colour:suit': '[curious] And what colour should it be?',
    'item:eyewear': '[playful] Something for your eyes?',
    'item:headgear': '[curious] Something on your head?',
    'item:back': '[playful] Anything on your back?',
    'item:shoes': '[cheerful] Now your shoes!',
    codename:
      '[excited, whispering] Now the best part. Every spy has a secret codename. Pick one, and say it out loud!',
  },
  older: {
    'item:body':
      '[calm] HQ needs your agent file. First, skin tone: pick the one closest to yours.',
    'item:face': '[calm] Now the face.',
    'item:hair': '[easy] Hair next.',
    'colour:hair': '[easy] Hair colour?',
    'item:suit': '[excited] Pick your field gear.',
    'colour:suit': '[curious] And the colour.',
    'item:eyewear': '[playful] Eyewear, or none?',
    'item:headgear': '[curious] Anything on your head?',
    'item:back': '[easy] Anything for your back?',
    'item:shoes': '[easy] Shoes, and we’re nearly done.',
    codename: '[excited, whispering] Last, your codename. Choose one and say it.',
  },
};

/** Alex at the end, with the chosen codename. */
export function doneLine(band: AgeBand, codename: string): string {
  return band === 'younger'
    ? `[delighted] Hello, Agent ${codename}! You look ready for anything.`
    : `[confident] Agent ${codename}. File complete. Welcome to the team.`;
}

/** Spoken buttons on every step besides the options themselves. */
export const NAV = {
  next: { id: '@next', label: 'Next', says: ['next', 'skip', 'keep it', 'this one is fine'] },
  back: { id: '@back', label: 'Back', says: ['back', 'go back', 'previous'] },
  surprise: { id: '@surprise', label: 'Surprise me', says: ['surprise', 'random', 'you pick'] },
  more: { id: '@more', label: 'Other names', says: ['other', 'more', 'different', 'new ones'] },
} as const;
